import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, Store, NgxsSimpleChange } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { FormTypeActions } from '../actions/form-type.actions';
import { FormType } from '../models/form-type.model';
import { FormTypeService } from 'src/app/services/form-type.service';
import { StorageService } from 'src/app/services/storage.service';
import { stateContentChanged, sameRows } from './state-content-changed'

export class FormTypeStateModel {
    is_refreshing: boolean
    form_type: FormType[]
    version?: string
}

@State<FormTypeStateModel>({
    name: 'form_type',
    defaults: {
        is_refreshing: true,
        form_type: []
    }
})
@Injectable()
export class FormTypeState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private formTypeService: FormTypeService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<FormTypeStateModel>) {                
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                     
                        await this.reloadStateFromStorage()                        
                    }
                })                           
            }
        }) 
    } 

    async ngxsOnChanges(change: NgxsSimpleChange) {                       
        if (this.storageService.is_initialized && stateContentChanged(change)) {
            const state_model: FormTypeStateModel = change.currentValue
            this.storageService.set('form_types', JSON.stringify(state_model))    
        }      
    }

    async reloadStateFromStorage() {        
        const json = await this.storageService.get('form_types');        
        if (json) {
            const state_model: FormTypeStateModel = JSON.parse(json)
            if (state_model) {
                this.store.dispatch(new FormTypeActions.Init(state_model));
                return
            }
        }
        this.store.dispatch(new FormTypeActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        // Send the version only with rows to go with it.
        const held = this.store.selectSnapshot(FormTypeState.getState)
        const ret = await this.formTypeService.getAll(held.form_type?.length ? held.version ?? '' : '')
        if (ret?.rows) {
          // The service has already run buildMany over the response, so the
          // rows arriving here are built models. Building them a second time
          // allocated a duplicate object for every row and, where buildOne
          // rebuilds children, re-walked those too.
          this.store.dispatch(new FormTypeActions.Set(ret.rows, ret.version))
          return true
        }
        // Failed, or the list held is still current.
        return false
    }
  
    @Selector()
    static getState(state: FormTypeStateModel) {
        return state
    }

    @Selector()
    static getIsRefreshing(state: FormTypeStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getAll(state: FormTypeStateModel) {
        return state.form_type
    }

    @Action(FormTypeActions.Refresh)
    async refresh({patchState, getState}: StateContext<FormTypeStateModel>, {  }:FormTypeActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false, form_type: sameRows(getState().form_type)})
        }
    }
    
    @Action(FormTypeActions.Init)
    init({setState, getState}: StateContext<FormTypeStateModel>, { payload }:FormTypeActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new FormTypeActions.Refresh())
    }

    @Action(FormTypeActions.Set)
    set({setState}: StateContext<FormTypeStateModel>, { payload, version }:FormTypeActions.Set) {             
        setState({    
            is_refreshing: false,        
            form_type: payload,
            version
        })
    }
}
